import React from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../Header';
import { LandingPage } from '../landing/LandingPage';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { AUTH_PATHS, AuthNavState } from './authPaths';
import { AuthChoicePage } from './AuthChoicePage';
import { PhoneStepForm } from './PhoneStepForm';
import { CodeStepForm } from './CodeStepForm';
import { RegistrationDetailsForm } from './RegistrationDetailsForm';

/**
 * Всё, что видит человек до кабинета: лендинг, развилка, две двери и анкета.
 *
 * Двери разведены до самого верха — до адресов. `/login` не заводит кабинет,
 * `/register` не пускает в существующий, и человек в любой момент видит по
 * адресу, что именно он делает.
 */

function AuthScreen({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <Header title={title} />
      {children}
    </>
  );
}

export const AuthRoutes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationPhone } = useAuth();

  const state = (location.state ?? {}) as AuthNavState;

  /**
   * Регистрация начата, но не доведена до конца: кабинет закрыт, пока анкета
   * не отправлена. Пустой номер — знаем только, что анкеты нет; вести в неё
   * некуда, черновик привязан к телефону, поэтому начинаем сначала.
   */
  const isRegistrationPending = registrationPhone !== null;
  const pendingPath = registrationPhone ? AUTH_PATHS.registerDetails : AUTH_PATHS.register;
  const { t } = useLanguage();

  return (
    <Routes>
      <Route
        path={AUTH_PATHS.choice}
        element={
          <AuthScreen title={t.auth.choiceTitle}>
            <AuthChoicePage />
          </AuthScreen>
        }
      />

      <Route
        path={AUTH_PATHS.login}
        element={
          <AuthScreen title={t.auth.loginTitle}>
            <PhoneStepForm
              mode="login"
              initialPhone={state.phone}
              notice={state.notice}
              onCodeSent={(phone) => navigate(AUTH_PATHS.loginVerify, { state: { phone } })}
              // Кабинета нет — не ошибка, а развилка: переносим номер
              // в регистрацию вместе с объяснением сервера.
              onWrongDoor={(phone, notice) =>
                navigate(AUTH_PATHS.register, { state: { phone, notice } })
              }
            />
          </AuthScreen>
        }
      />

      <Route
        path={AUTH_PATHS.loginVerify}
        element={
          state.phone ? (
            <AuthScreen title={t.auth.confirmTitle}>
              <CodeStepForm
                mode="login"
                phone={state.phone}
                onSuccess={() => navigate('/')}
                onBack={() => navigate(AUTH_PATHS.login, { state: { phone: state.phone } })}
                onWrongDoor={(phone, notice) =>
                  navigate(AUTH_PATHS.register, { state: { phone, notice } })
                }
              />
            </AuthScreen>
          ) : (
            <Navigate to={AUTH_PATHS.login} replace />
          )
        }
      />

      <Route
        path={AUTH_PATHS.register}
        element={
          <AuthScreen title={t.auth.registerTitle}>
            <PhoneStepForm
              mode="registration"
              initialPhone={state.phone}
              notice={state.notice}
              onCodeSent={(phone) => navigate(AUTH_PATHS.registerVerify, { state: { phone } })}
              // Кабинет уже есть — переносим номер во вход.
              onWrongDoor={(phone, notice) =>
                navigate(AUTH_PATHS.login, { state: { phone, notice } })
              }
            />
          </AuthScreen>
        }
      />

      <Route
        path={AUTH_PATHS.registerVerify}
        element={
          state.phone ? (
            <AuthScreen title={t.auth.confirmTitle}>
              <CodeStepForm
                mode="registration"
                phone={state.phone}
                onSuccess={() => navigate(AUTH_PATHS.registerDetails)}
                onBack={() => navigate(AUTH_PATHS.register, { state: { phone: state.phone } })}
                onWrongDoor={(phone, notice) =>
                  navigate(AUTH_PATHS.login, { state: { phone, notice } })
                }
              />
            </AuthScreen>
          ) : (
            <Navigate to={AUTH_PATHS.register} replace />
          )
        }
      />

      <Route
        path={AUTH_PATHS.registerDetails}
        element={
          registrationPhone ? (
            <AuthScreen title={t.auth.orgDetailsTitle}>
              <RegistrationDetailsForm phone={registrationPhone} onSuccess={() => navigate('/')} />
            </AuthScreen>
          ) : (
            <Navigate to={AUTH_PATHS.register} replace />
          )
        }
      />

      <Route
        path="*"
        element={
          isRegistrationPending ? (
            <Navigate to={pendingPath} replace />
          ) : (
            <LandingPage />
          )
        }
      />
    </Routes>
  );
};
